import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { storage } from '@/utils/storage';
import { tokenHasPermission } from '@/features/auth/utils/jwtPermissions';
import {
  createAdminUser,
  getAdminUser,
  toAdminUsersApiError,
  updateAdminUser,
  updateAdminUserStatus,
} from '../api/adminUsersApi';
import UserFormModal, { type UserModalMode } from '../components/UserFormModal';
import UserStats from '../components/UserStats';
import UserTable from '../components/UserTable';
import UserToolbar from '../components/UserToolbar';
import { useAdminUsers } from '../hooks/useAdminUsers';
import type { AdminUser, CreateUserInput, UpdateUserInput } from '../types';

const AdminUsersPage: React.FC = () => {
  const { t } = useTranslation();
  const {
    query,
    pageData,
    statistics,
    isInitialLoading,
    isTableLoading,
    isStatisticsLoading,
    loadError,
    setSearch,
    setRole,
    setStatus,
    setPage,
    refresh,
    retry,
  } = useAdminUsers();
  const [searchValue, setSearchValue] = useState(query.search ?? '');
  const [modalMode, setModalMode] = useState<UserModalMode>('create');
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingUserId, setPendingUserId] = useState<number | null>(null);
  const [statusTarget, setStatusTarget] = useState<AdminUser | null>(null);
  const token = storage.getAccessToken();
  const canReadDetail = tokenHasPermission(token, 'USER_READ_DETAIL');
  const canCreate = tokenHasPermission(token, 'USER_CREATE');
  const canUpdate = tokenHasPermission(token, 'USER_UPDATE');
  const canUpdateStatus = tokenHasPermission(token, 'USER_UPDATE_STATUS');

  useEffect(() => {
    setSearchValue(query.search ?? '');
  }, [query.search]);

  useEffect(() => {
    if (searchValue.trim() === (query.search ?? '')) return undefined;
    const timer = window.setTimeout(() => setSearch(searchValue), 400);
    return () => window.clearTimeout(timer);
  }, [query.search, searchValue, setSearch]);

  const openCreateModal = () => {
    setModalMode('create');
    setSelectedUser(null);
    setIsModalOpen(true);
  };

  const openUserModal = async (user: AdminUser, mode: UserModalMode) => {
    setPendingUserId(user.id);
    try {
      const detail = await getAdminUser(user.id);
      setSelectedUser(detail);
      setModalMode(mode);
      setIsModalOpen(true);
    } catch (error: unknown) {
      message.error(toAdminUsersApiError(error).message);
    } finally {
      setPendingUserId(null);
    }
  };

  const submitUser = async (input: CreateUserInput | UpdateUserInput) => {
    setIsSubmitting(true);
    try {
      if (modalMode === 'create') {
        await createAdminUser(input as CreateUserInput);
        message.success(t('admin.users.notices.created'));
      } else if (selectedUser) {
        await updateAdminUser(selectedUser.id, input as UpdateUserInput);
        message.success(t('admin.users.notices.updated'));
      }
      await refresh();
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmStatusChange = async () => {
    if (!statusTarget) return;
    setPendingUserId(statusTarget.id);
    try {
      await updateAdminUserStatus(statusTarget.id, !statusTarget.active);
      message.success(t(statusTarget.active
        ? 'admin.users.notices.deactivated'
        : 'admin.users.notices.activated'));
      setStatusTarget(null);
      await refresh();
    } catch (error: unknown) {
      message.error(toAdminUsersApiError(error).message);
    } finally {
      setPendingUserId(null);
    }
  };

  return (
    <div className="mx-auto flex min-h-full w-full min-w-0 max-w-[1600px] flex-col gap-md sm:gap-lg">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-h1 text-h1 text-on-surface">{t('admin.users.title')}</h1>
          <p className="mt-1 text-body-md text-secondary">{t('admin.users.subtitle')}</p>
        </div>
        <div className="inline-flex w-fit items-center gap-2 rounded-full bg-primary-fixed px-3 py-1.5 font-label-sm text-primary">
          <span className="material-symbols-outlined text-[18px]">verified_user</span>
          {t('admin.users.permission_notice')}
        </div>
      </header>

      <UserStats statistics={statistics} isLoading={isStatisticsLoading} />

      <section className="flex min-h-[520px] min-w-0 flex-1 flex-col overflow-hidden rounded-[20px] border border-outline-variant/70 bg-white shadow-sm">
        <div className="border-b border-outline-variant px-4 py-4 sm:px-5">
          <h2 className="font-h2 text-h2 text-on-surface">{t('admin.users.management_title')}</h2>
          <p className="mt-1 text-body-sm text-secondary">{t('admin.users.management_description')}</p>
        </div>
        <UserToolbar
          activeRole={query.role}
          activeStatus={query.status}
          searchValue={searchValue}
          canCreate={canCreate}
          onRoleChange={setRole}
          onStatusChange={setStatus}
          onSearchChange={setSearchValue}
          onCreate={openCreateModal}
        />
        {loadError && pageData.data.length > 0 && (
          <div className="flex items-center justify-between gap-3 border-b border-error/20 bg-error-container/35 px-4 py-2 text-body-sm text-error" role="alert">
            <span>{loadError}</span>
            <button type="button" className="shrink-0 font-label-sm text-primary hover:underline" onClick={() => void retry()}>
              {t('admin.users.actions.retry')}
            </button>
          </div>
        )}
        <UserTable
          users={pageData.data}
          currentPage={pageData.currentPage}
          totalPages={pageData.totalPages}
          totalElements={pageData.totalElements}
          isLoading={isTableLoading}
          isInitialLoading={isInitialLoading}
          error={loadError}
          canReadDetail={canReadDetail}
          canUpdate={canUpdate}
          canUpdateStatus={canUpdateStatus}
          pendingUserId={pendingUserId}
          onPageChange={setPage}
          onView={(user) => void openUserModal(user, 'view')}
          onEdit={(user) => void openUserModal(user, 'edit')}
          onToggleStatus={setStatusTarget}
          onRetry={() => void retry()}
        />
      </section>

      <UserFormModal
        open={isModalOpen}
        mode={modalMode}
        user={selectedUser}
        isSubmitting={isSubmitting}
        onClose={() => !isSubmitting && setIsModalOpen(false)}
        onSubmit={submitUser}
      />

      {statusTarget && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/45 p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && pendingUserId === null) setStatusTarget(null);
          }}
        >
          <section className="w-full max-w-md rounded-[20px] border border-outline-variant bg-white p-5 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="status-dialog-title">
            <div className="flex items-start gap-3">
              <span className={`material-symbols-outlined rounded-full p-2 ${statusTarget.active ? 'bg-error-container text-error' : 'bg-primary-fixed text-primary'}`}>
                {statusTarget.active ? 'person_off' : 'how_to_reg'}
              </span>
              <div>
                <h2 id="status-dialog-title" className="font-h3 text-h3 text-on-surface">
                  {t(statusTarget.active ? 'admin.users.status_dialog.deactivate_title' : 'admin.users.status_dialog.activate_title')}
                </h2>
                <p className="mt-2 text-body-md text-secondary">
                  {t(statusTarget.active ? 'admin.users.status_dialog.deactivate_description' : 'admin.users.status_dialog.activate_description', { user: statusTarget.username })}
                </p>
              </div>
            </div>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" className="min-h-10 rounded-xl border border-outline-variant px-4 font-label-md text-secondary" disabled={pendingUserId !== null} onClick={() => setStatusTarget(null)}>
                {t('admin.users.actions.cancel')}
              </button>
              <button type="button" className={`min-h-10 rounded-xl px-4 font-label-md text-white disabled:opacity-60 ${statusTarget.active ? 'bg-error' : 'bg-primary'}`} disabled={pendingUserId !== null} onClick={() => void confirmStatusChange()}>
                {pendingUserId !== null ? t('admin.users.actions.saving') : t(statusTarget.active ? 'admin.users.actions.deactivate' : 'admin.users.actions.activate')}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
